import cx from 'clsx';
import { format } from 'date-fns';
import { fromZonedTime } from 'date-fns-tz';
import React, { useEffect } from 'react';
import { v4 as uuidv4 } from 'uuid';

import TableEmptyRowExtended from '@components/common/TableEmptyRowExtended/TableEmptyRowExtended';
import TableLoading from '@components/common/TableLoading/TableLoading';
import TableLoadingTrigger from '@components/common/TableLoadingTrigger/TableLoadingTrigger';

import { useDebouncedValue } from '@hooks/useDebouncedValue';
import { useIntersectionObserver } from '@hooks/useIntersectionObserver';
import useTranslation from '@hooks/useTranslation';

import { useUserProfileQuery } from '@services/user/useUserProfileQuery';

import { useLocationsLogListInfiniteQuery } from '../../services/useLocationsLogListInfiniteQuery';
import LocationsLogTableSkeleton from './LocationsLogSkeleton';
import style from './style.module.scss';

interface IProps {
  searchValue: string;
}

const LocationsLog: React.FC<IProps> = ({ searchValue }) => {
  const { t } = useTranslation();
  const { data: profileData } = useUserProfileQuery();
  const [debouncedSearchValue] = useDebouncedValue(searchValue, 500);

  const dateTimeFormat = `${profileData?.dateFormat.date} ${profileData?.dateFormat.time}`;
  const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const {
    data: locationsLogsQuery,
    fetchNextPage,
    isFetching,
    hasNextPage,
    isLoading,
  } = useLocationsLogListInfiniteQuery({
    perPage: 20,
    searchValue: debouncedSearchValue,
  });

  const locationsLogsList = locationsLogsQuery?.pages?.flatMap((page) => page.data) ?? [];

  const [ref, isInViewPort] = useIntersectionObserver({
    threshold: 0.1,
    reInitializeOnChange: isFetching,
  });

  useEffect(() => {
    if (isInViewPort && hasNextPage && !isFetching) {
      fetchNextPage();
    }
  }, [isInViewPort, hasNextPage, isFetching]);

  return (
    <div className={style.locationsLogBlock}>
      <div className={style.locationsLogBlockTitle}>{t('Location Change Log')}</div>
      <div className={style.locationsLogBlockContainer}>
        <table className={style.locationsLogsTable} cellSpacing="0" cellPadding="0">
          <thead className={style.locationsLogsTableHead}>
            <tr>
              <th>{t('Event')}</th>
              <th className={style.hideColumn}>{t('Info')}</th>
              <th>{t('Created At')}</th>
            </tr>
          </thead>
          <tbody className={style.locationsLogsTableBody}>
            {isLoading ? (
              <LocationsLogTableSkeleton />
            ) : locationsLogsList.length ? (
              locationsLogsList.map((row) => (
                <tr key={uuidv4()} className={style.locationsLogsTableBodyRow}>
                  <td>
                    <div data-qa="event">{t(row.action)}</div>
                    <div className={cx(style.detailsInfo)}>
                      <span className={style.details}>{t('Details')}</span> : {row.notes}
                    </div>
                  </td>
                  <td className={style.hideColumn}>
                    <div className={style.messageCell} data-qa="details">
                      {row.notes}
                    </div>
                  </td>
                  <td>
                    <span className={style.dateTimeCell} data-qa="date-time">
                      {format(fromZonedTime(row.created_at, userTimezone), dateTimeFormat)}
                    </span>
                    <div className={style.userNameCell} data-qa="by-user">
                      {t('by')} {row.user_name} {t('from')} {+row.source_id === 5 ? 'FSD' : 'SSD'}
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <TableEmptyRowExtended text={t('No location logs were found.')} />
            )}
          </tbody>
          <tfoot>
            <TableLoadingTrigger show={!isFetching} ref={ref} />
            <TableLoading show={isFetching && !isLoading} />
          </tfoot>
        </table>
      </div>
    </div>
  );
};

export default LocationsLog;
